import { useState, useRef, useCallback } from 'react';
import { resolveApiUrl } from '@/services/api';

interface UseVoiceInputOptions {
  language?: string;
  /** Called with the cumulative transcript (final + interim) on each update */
  onTranscript?: (text: string) => void;
  /** Called when recording stops with the final confirmed transcript */
  onFinalTranscript?: (text: string) => void;
  onError?: (error: string) => void;
}

interface UseVoiceInputReturn {
  isRecording: boolean;
  isTranscribing: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  toggleRecording: () => void;
  isSupported: boolean;
}

export function useVoiceInput(options: UseVoiceInputOptions = {}): UseVoiceInputReturn {
  const { language = 'ca', onTranscript, onFinalTranscript, onError } = options;

  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const finalTranscriptRef = useRef('');
  const activeRef = useRef(false);

  const isSupported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

  const cleanup = useCallback(() => {
    // Mark inactive FIRST to ignore any buffered messages
    activeRef.current = false;

    // Stop MediaRecorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;

    // Release mic
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;

    // Close WebSocket
    if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) {
      wsRef.current.close();
    }
    wsRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    if (isRecording) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Build WebSocket URL to our backend proxy
      const apiUrl = resolveApiUrl(); // e.g. http://localhost:8001/api
      const url = new URL(apiUrl);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      url.pathname = '/ws/speech';
      url.search = `?language=${language}`;
      const wsUrl = url.toString();

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      finalTranscriptRef.current = '';
      activeRef.current = true;

      ws.onopen = () => {
        // WS to backend is open, but we wait for Deepgram to be ready
        // before starting the MediaRecorder (see onmessage 'ready' handler)
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Backend signals Deepgram is ready → start recording now
          if (data.type === 'ready') {
            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
              ? 'audio/webm;codecs=opus'
              : MediaRecorder.isTypeSupported('audio/webm')
                ? 'audio/webm'
                : 'audio/mp4';

            const mediaRecorder = new MediaRecorder(stream, { mimeType });
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (e) => {
              if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) {
                ws.send(e.data);
              }
            };

            mediaRecorder.start(250);
            setIsRecording(true);
            setIsTranscribing(true);
            return;
          }

          // Handle Deepgram transcription results (ignore if stopped)
          if (data.type === 'Results' && activeRef.current) {
            const alt = data.channel?.alternatives?.[0];
            const transcript = alt?.transcript || '';

            if (transcript) {
              if (data.is_final) {
                // Accumulate final (confirmed) transcript
                finalTranscriptRef.current +=
                  (finalTranscriptRef.current ? ' ' : '') + transcript;
                onTranscript?.(finalTranscriptRef.current);
              } else {
                // Show interim (partial) result appended to finals
                const combined = finalTranscriptRef.current
                  ? finalTranscriptRef.current + ' ' + transcript
                  : transcript;
                onTranscript?.(combined);
              }
            }
          } else if (data.type === 'error') {
            onError?.(data.message || 'Error de streaming');
            cleanup();
            setIsRecording(false);
            setIsTranscribing(false);
          }
        } catch {
          // Ignore non-JSON messages
        }
      };

      ws.onclose = () => {
        // Deliver final transcript when stream closes (only if not already cleaned up)
        if (finalTranscriptRef.current && activeRef.current) {
          onFinalTranscript?.(finalTranscriptRef.current);
        }
        activeRef.current = false;
        setIsTranscribing(false);
      };

      ws.onerror = () => {
        onError?.('Error de connexió amb el servei de veu');
        cleanup();
        setIsRecording(false);
        setIsTranscribing(false);
      };
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        onError?.('Permís de micròfon denegat. Activa\'l a la configuració del navegador.');
      } else {
        onError?.('No s\'ha pogut accedir al micròfon.');
      }
    }
  }, [isRecording, language, onTranscript, onFinalTranscript, onError, cleanup]);

  const stopRecording = useCallback(() => {
    if (!isRecording) return;

    setIsRecording(false);
    cleanup();
  }, [isRecording, cleanup]);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  return {
    isRecording,
    isTranscribing,
    startRecording,
    stopRecording,
    toggleRecording,
    isSupported,
  };
}
